import { useOrganization } from '@clerk/clerk-react';
import {
  clientToShow,
  coverOf,
  shownInApp,
  useProjectDetail,
  useProjects,
  useProjectsList,
  type ProjectCard,
  type ProjectDetail,
  type ProjectsSort,
  type ProjectsTab,
} from '@flobi/asset-library/projects';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../editor/chrome/icons';
import { PlaceholderChips } from '../home/ProjectChips';
import { FolderCard, filesLine, pocketPictures } from '../home/ProjectStack';
import { Face } from '../home/parts';
import { PhoneEmptyState, usePhone } from '../home/PhoneEmptyState';
import '../home/projectCards.css';
import { relativeWhen, type ProjectSummary } from '../home/rows';
import { openProjectShare } from '../share/artworkShare';
import { Button, IconButton, SegmentedControl } from '../ui/controls';
import { Menu, type MenuEntry } from '../ui/menu';
import { setPinned, usePins } from './pins';
import { countFiles, projectSummaries, useBrandOf } from './projectsApi';

/**
 * Artwork's two project pages, in Flow's projects structure (Tarek, 13 Oct) drawn with Artwork's
 * own parts — the 72 px header, the segmented control, Artwork's folder (the original of Flow's),
 * Artwork's menus and buttons. The shared kit supplies only the data (`useProjectsList`,
 * `useProjectDetail`, `coverOf`, `brandById`) and its sheets (New project, Project settings).
 *
 *   /?view=projects     "Projects" + New Project; Recent · Owned by Me · Shared with Me ·
 *                       Workspace and the sort on the right; folders with each project's cover,
 *                       brand and "N Artwork files", ⋯ beside the name
 *   /?project=<id>      "Projects › name", one quiet line (client · N files · updated …); people,
 *                       Share, ⋯ and New File (made in the project); then Artworks and its sort
 */

/** The kit's tabs and sorts, in Artwork's words. */
const TAB_LABEL: Record<ProjectsTab, string> = { recent: 'Recent', mine: 'Owned by Me', shared: 'Shared with Me', workspace: 'Workspace' };
const SORT_LABEL: Record<ProjectsSort, string> = { recent: 'Last Edited', name: 'Name', created: 'Newest' };

/** How a project's files are ordered on its page. */
export type FileSort = 'edited' | 'name';
const FILE_SORTS: readonly { id: FileSort; label: string }[] = [
  { id: 'edited', label: 'Last Edited' },
  { id: 'name', label: 'Name' },
];

/**
 * The ⋯ a project has, on its folder and on its page: Project Settings…, Open in Projects ↗,
 * Pin / Unpin, Hide from My Artwork. Each only when it can apply. Pin is the star Flow and the
 * Projects app keep (projects-back `project.favorite.*`); pinned projects lead the Home.
 */
function useProjectMenu({ onHidden, onSaved }: { onHidden: (project: ProjectCard) => void; onSaved?: (project: ProjectDetail) => void }) {
  const { app, adapter, links, openSettings, bump } = useProjects();
  const { organization } = useOrganization();
  const pins = usePins(organization?.id ?? '');
  const [failure, setFailure] = useState<string | null>(null);

  const isPinned = useCallback((id: string) => !!pins?.some((one) => one.id === id), [pins]);
  const entries = (project: ProjectCard): MenuEntry[] => {
    const pinned = isPinned(project.id);
    return [
      ...(project.canManage ? [{ id: 'settings', label: 'Project Settings…', onSelect: () => openSettings(project, { onSaved }) }] : []),
      ...(links.openInProjects ? [{ id: 'projects', label: 'Open in Projects ↗', onSelect: () => links.openInProjects!(project.id) }] : []),
      ...(pins
        ? [
            {
              id: 'pin',
              label: pinned ? 'Unpin' : 'Pin',
              onSelect: () => {
                setFailure(null);
                setPinned({ id: project.id, name: project.name || 'Untitled Project', cover: coverOf(project) }, !pinned).catch(() =>
                  setFailure(pinned ? 'Couldn’t unpin it. Try again.' : 'Couldn’t pin it. Try again.'),
                );
              },
            },
          ]
        : []),
      { kind: 'separator' as const },
      {
        id: 'hide',
        label: 'Hide from My Artwork',
        onSelect: () => {
          setFailure(null);
          // Gone from here at once; the call follows, and a refusal says so.
          onHidden(project);
          Promise.resolve(adapter.hideForMe(project.id, app, true)).then(bump, () => {
            setFailure('Couldn’t hide it from Artwork. Try again.');
            bump();
          });
        },
      },
    ];
  };
  return { entries, failure };
}

/** The rail's sort: the chosen order's words and the up-and-down chevrons, opening Artwork's menu. */
function SortMenu<T extends string>({ options, value, onChange }: { options: readonly { id: T; label: string }[]; value: T; onChange: (id: T) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const label = options.find((one) => one.id === value)?.label ?? options[0]?.label ?? '';
  if (options.length < 2) return null;
  return (
    <>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label={`Sort: ${label}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        className={`flex h-[28px] shrink-0 items-center gap-[4px] rounded-full px-[10px] text-[13px] transition-colors hover:bg-fill-2 hover:text-label ${anchor ? 'bg-fill-2 text-label' : 'text-label-2'}`}
      >
        {label}
        <Icon name="chevron-up-down" size={12} />
      </button>
      {anchor ? (
        <Menu
          open
          anchor={anchor}
          onClose={() => setAnchor(null)}
          align="end"
          variant="screen"
          width={220}
          current={value}
          items={options.map((one) => ({ id: one.id, label: one.label, checked: one.id === value, onSelect: () => one.id !== value && onChange(one.id) }))}
        />
      ) : null}
    </>
  );
}

/** A folder's ⋯, beside its name. */
function FolderMore({ name, items, onOpenChange }: { name: string; items: () => MenuEntry[]; onOpenChange: (open: boolean) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        icon="more"
        label={`More for ${name}`}
        size={28}
        selected={!!anchor}
        onClick={(event) => {
          setAnchor(event.currentTarget);
          onOpenChange(true);
        }}
      />
      {anchor ? (
        <Menu
          open
          anchor={anchor}
          onClose={() => {
            setAnchor(null);
            onOpenChange(false);
          }}
          align="end"
          variant="screen"
          items={items()}
        />
      ) : null}
    </>
  );
}

/** A project on the Projects page: Artwork's folder with its cover, brand and files, ⋯ beside the name. */
function ProjectFolder({ project, summary, dim, onOpen, menu }: { project: ProjectCard; summary: ProjectSummary | null | undefined; dim: boolean; onOpen: () => void; menu: () => MenuEntry[] }) {
  const brandOf = useBrandOf();
  const [held, setHeld] = useState(false);
  const files = summary === undefined ? null : (summary?.files ?? 0);
  const from = project.workspace ? `From ${project.workspace.name || 'another workspace'}` : null;
  const line = dim ? 'Not shown in Artwork' : from ?? (summary === undefined ? '' : filesLine(files));
  const name = project.name || 'Untitled Project';
  return (
    <FolderCard
      name={name}
      line={line}
      covers={pocketPictures(coverOf(project), summary?.thumbnails ?? [], files)}
      brand={brandOf(project.brands?.[0]?.id)}
      onOpen={onOpen}
      held={held}
      dim={dim}
      trailing={<FolderMore name={name} items={menu} onOpenChange={setHeld} />}
    />
  );
}

/** Every project shown in Artwork (`/?view=projects`). */
export function AllProjects({ onBack, onOpen }: { onBack: () => void; onOpen: (id: string) => void }) {
  const { app, adapter, openCreate } = useProjects();
  /** A phone draws an empty or failed list as the suite's (pick E); a laptop keeps these lines. */
  const phone = usePhone();
  const [tab, setTab] = useState<ProjectsTab>('recent');
  const [sort, setSort] = useState<ProjectsSort>('recent');
  const [reveal, setReveal] = useState(false);
  const list = useProjectsList({ tab, sort, pageSize: 24, includeHidden: reveal });
  /** Changes made here (settings saved, hidden) shown at once, over what the list last read. */
  const [saved, setSaved] = useState<Record<string, ProjectDetail>>({});
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  /** Dim the grid only while a tab or sort the person chose is loading — never for a quiet re-read. */
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    if (list.status !== 'loading') setSwitching(false);
  }, [list.status]);
  const box = useRef<HTMLElement>(null);
  /** The tallest the list has been since the page opened, so a shorter tab never pulls the page up. */
  const [hold, setHold] = useState(0);

  // A tab this workspace no longer offers (the second member left) reads as Recent.
  useEffect(() => {
    if (list.tab !== tab) setTab(list.tab);
  }, [list.tab, tab]);

  const hidden = (project: ProjectCard) => setGone((was) => new Set(was).add(project.id));
  const onSaved = (project: ProjectDetail) => {
    setSaved((was) => ({ ...was, [project.id]: project }));
    if (!shownInApp(project, app) && !reveal) hidden(project);
  };
  const { entries, failure } = useProjectMenu({ onHidden: hidden, onSaved });

  const items = useMemo(() => list.items.filter((one) => !gone.has(one.id)).map((one) => (saved[one.id] ? { ...one, ...saved[one.id] } : one)), [list.items, gone, saved]);

  // "N Artwork files" and the newest pictures under each folder: one read for the page.
  const [summaries, setSummaries] = useState<Record<string, ProjectSummary | null>>({});
  const ids = items.map((one) => one.id).join(',');
  useEffect(() => {
    const wanted = ids ? ids.split(',') : [];
    if (!wanted.length) return undefined;
    let live = true;
    projectSummaries(wanted).then(
      (found) => live && setSummaries((was) => ({ ...was, ...found })),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [ids]);

  const create = () => void openCreate({ onCreated: (project) => onOpen(project.id) });
  const change = (apply: () => void) => {
    setHold((was) => Math.max(was, box.current?.offsetHeight ?? 0));
    setSwitching(true);
    apply();
  };

  const failed = list.status === 'error';
  const loading = list.status === 'loading' && !list.refreshing;
  const count = list.status === 'ready' ? `${items.length}${list.hasMore ? '+' : ''}` : undefined;

  return (
    <>
      <header className="mb-[8px] flex h-[72px] items-center gap-[12px]">
        <IconButton icon="back" label="Back" size={30} onClick={onBack} />
        <div className="min-w-0 flex-1">
          <h1 className="flex min-w-0 items-baseline gap-[10px] text-[26px] font-bold leading-[32px] tracking-[-0.01em]">
            Projects
            {count !== undefined ? <span className="shrink-0 text-[15px] font-medium tabular-nums text-label-3">{count}</span> : null}
          </h1>
        </div>
        <NewButton onClick={create}>New Project</NewButton>
      </header>
      <section ref={box} style={hold ? { minHeight: hold } : undefined}>
        {failed ? null : (
          <div className="mb-[20px] flex items-center gap-[12px]">
            <div className="min-w-0 flex-1">
              {list.tabs.length > 1 ? (
                <SegmentedControl
                  label="Whose projects"
                  className={`w-full ${list.tabs.length > 3 ? 'max-w-[480px]' : 'max-w-[380px]'}`}
                  value={list.tab}
                  onChange={(next) => change(() => setTab(next))}
                  segments={list.tabs.map((one) => ({ value: one.id, label: TAB_LABEL[one.id] ?? one.label }))}
                />
              ) : null}
            </div>
            <SortMenu options={list.sortOptions.map((one) => ({ id: one.id, label: SORT_LABEL[one.id] ?? one.label }))} value={sort} onChange={(next) => change(() => setSort(next))} />
          </div>
        )}
        {failure ? (
          <p role="alert" className="mb-[16px] text-[13px] text-label-2">
            {failure}
          </p>
        ) : null}
        {failed && phone ? (
          <PhoneEmptyState tone="error" title="Couldn’t load your projects" onRetry={list.retry} />
        ) : failed ? (
          <LoadFailed words={list.error || 'Projects didn’t load'} onRetry={list.retry} />
        ) : loading ? (
          <PlaceholderChips count={8} />
        ) : items.length === 0 && phone ? (
          list.tab === 'shared' ? (
            <PhoneEmptyState title="Nothing shared with you yet" line="Projects other workspaces share with you show up here." />
          ) : (
            <PhoneEmptyState
              title="No projects yet"
              line="A project keeps one client’s or one idea’s files together."
              starts={[{ id: 'project', icon: 'project', label: 'New project', hint: 'Its files, together', onClick: create }]}
            />
          )
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-[12px] py-[48px]">
            <p className="text-center text-[13px] text-label-2">{list.tab === 'shared' ? 'Projects other workspaces share with you show here.' : 'Start a project to keep your files together.'}</p>
            {list.tab === 'shared' ? null : <Button onClick={create}>New Project</Button>}
          </div>
        ) : (
          <ul className={`home-project-grid transition-opacity duration-150 ${switching && list.refreshing ? 'opacity-50' : ''}`}>
            {items.map((project) => (
              <li key={project.id}>
                <ProjectFolder project={project} summary={summaries[project.id]} dim={!shownInApp(project, app)} onOpen={() => onOpen(project.id)} menu={() => entries(project)} />
              </li>
            ))}
          </ul>
        )}
        {!failed && !loading && list.hasMore ? (
          <div className="mt-[24px] flex justify-center">
            <Button onClick={() => void list.loadMore()} disabled={list.loadingMore}>
              {list.moreFailed ? 'Try Again' : 'Show More'}
            </Button>
          </div>
        ) : null}
        {!failed && list.hiddenCount > 0 ? (
          <p className="mt-[24px] text-center text-[12px] text-label-2">
            {reveal ? `Showing ${plural(list.hiddenCount)} that ${list.hiddenCount === 1 ? 'isn’t' : 'aren’t'} shown in Artwork.` : `${plural(list.hiddenCount)} ${list.hiddenCount === 1 ? 'isn’t' : 'aren’t'} shown in Artwork.`}{' '}
            {adapter.capabilities?.revealHidden ? (
              <button type="button" onClick={() => setReveal((was) => !was)} className="text-accent-text hover:underline">
                {reveal ? 'Hide Again' : 'Show'}
              </button>
            ) : null}
          </p>
        ) : null}
      </section>
    </>
  );
}

const plural = (n: number) => `${n} ${n === 1 ? 'project' : 'projects'}`;

/** Artwork's violet capsule (New File's), for New Project. */
function NewButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[32px] shrink-0 items-center gap-[6px] rounded-full bg-accent pl-[11px] pr-[13px] text-[13px] font-semibold text-on-accent transition-colors hover:bg-accent-hover"
    >
      <Icon name="plus" size={14} />
      {children}
    </button>
  );
}

function LoadFailed({ words, onRetry }: { words: string; onRetry: () => void }) {
  return (
    <p role="alert" className="py-[16px] text-[13px] text-label-2">
      {words} ·{' '}
      <button type="button" onClick={onRetry} className="text-accent-text hover:underline">
        Try again
      </button>
    </p>
  );
}

/** "updated 2 days ago" / "updated yesterday" / "updated 3 Oct". */
function updatedLine(iso: string | null): string {
  const said = relativeWhen(iso);
  if (!said) return '';
  return `updated ${/^(Just|Yesterday)/.test(said) ? said.charAt(0).toLowerCase() + said.slice(1) : said}`;
}

/**
 * One project's page (`/?project=<id>`): "Projects › name" and one quiet line under it; the
 * project's people, Share, ⋯ and New File (made in this project) on the right; then the Artworks
 * rail (its count, the sort) over the page's files (`children`). A project that has gone says so;
 * a failed read says "This project didn't load", never an empty page.
 */
export function ProjectPage({
  projectId,
  onAll,
  onGone,
  newFile,
  newest,
  sort,
  onSort,
  children,
}: {
  projectId: string;
  /** "Projects" in the title: every project. */
  onAll: () => void;
  onGone: () => void;
  newFile: ReactNode;
  /** When the newest file on the page last changed, for "updated …". */
  newest: string | null;
  sort: FileSort;
  onSort: (sort: FileSort) => void;
  children: ReactNode;
}) {
  const { app, shape, workspace, version } = useProjects();
  const { project, status, retry } = useProjectDetail(projectId);
  const phone = usePhone();
  const [files, setFiles] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  /** Saved in Project settings: shown at once, before the kit reads the project again. */
  const [saved, setSaved] = useState<ProjectDetail | null>(null);

  useEffect(() => {
    let live = true;
    countFiles([projectId]).then(
      (counts) => live && setFiles(counts[projectId] ?? 0),
      () => live && setFiles(null),
    );
    return () => {
      live = false;
    };
  }, [projectId, version]);

  const onSaved = (next: ProjectDetail) => {
    setSaved(next);
    // Show in Artwork turned off: this page is no longer Artwork's to show.
    if (!shownInApp(next, app)) onGone();
  };
  const { entries, failure } = useProjectMenu({ onHidden: onGone, onSaved });

  const read: ProjectDetail | null = project?.id === projectId ? project : null;
  const shown: ProjectDetail | null = read ? (saved?.id === read.id ? { ...read, ...saved } : read) : null;

  const meta = useMemo(() => {
    if (!shown) return '';
    const parts: string[] = [];
    const client = clientToShow(shown);
    if (client && client.trim().toLowerCase() !== 'internal') parts.push(client);
    if (files !== null) parts.push(`${files} ${files === 1 ? 'file' : 'files'}`);
    const at = Math.max(Date.parse(shown.updatedAt || '') || 0, Date.parse(newest || '') || 0);
    const updated = at ? updatedLine(new Date(at).toISOString()) : '';
    if (updated) parts.push(updated);
    return parts.join(' · ');
  }, [shown, files, newest]);

  const people = shown ? (shown.allPeople?.length ? shown.allPeople : shown.people) : [];
  const peopleTotal = shown ? Math.max(shown.peopleCount || 0, people.length) : 0;
  const members = new Map((workspace?.members ?? []).map((one) => [String(one.id), one]));

  const title = (
    <h1 className="flex min-w-0 items-baseline gap-[6px] text-[26px] font-bold leading-[32px] tracking-[-0.01em]">
      <button type="button" onClick={onAll} className="shrink-0 text-label-3 transition-colors hover:text-label">
        Projects
      </button>
      <span aria-hidden className="shrink-0 self-center text-label-3">
        <Icon name="chevron-right" size={18} />
      </span>
      {shown ? <span className="truncate">{shown.name}</span> : status === 'loading' ? <span aria-busy="true" className="block h-[24px] w-[200px] max-w-full self-center rounded-[6px] bg-fill-2" /> : null}
    </h1>
  );

  return (
    <>
      <header className="mb-[8px] flex min-h-[72px] items-center gap-[12px] py-[10px]">
        <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
          {title}
          {/* Height held so the line filling in never moves the rail. */}
          <p className="h-[18px] truncate text-[13px] leading-[18px] text-label-2">{meta}</p>
        </div>
        {shown ? (
          <div className="flex shrink-0 items-center gap-[8px]">
            {shape.people && peopleTotal > 1 ? (
              <span className="flex items-center max-sm:hidden" role="img" aria-label={`${peopleTotal} people`}>
                {people.slice(0, 4).map((id, index) => {
                  const one = members.get(String(id));
                  return (
                    <span key={id} className={index ? '-ml-[6px]' : ''}>
                      <Face owner={{ id: String(id), name: one?.name ?? null, avatarUrl: one?.avatarUrl ?? null }} size={26} />
                    </span>
                  );
                })}
                {peopleTotal > 4 ? <span className="ml-[6px] text-[13px] font-medium tabular-nums text-label-2">+{peopleTotal - 4}</span> : null}
              </span>
            ) : null}
            {shape.people ? (
              <Button className="max-sm:hidden" onClick={() => openProjectShare({ id: shown.id, name: shown.name })}>
                Share
              </Button>
            ) : null}
            <IconButton icon="more" label="Project options" size={30} selected={!!anchor} onClick={(event) => setAnchor(event.currentTarget)} />
            {/* A phone's header has no room for Share, so the ⋯ carries it (the responsive pass, 2026-10-26). */}
            {anchor ? <Menu open anchor={anchor} onClose={() => setAnchor(null)} align="end" variant="screen" items={[
              ...(phone && shape.people ? [{ id: 'share', label: 'Share…', onSelect: () => openProjectShare({ id: shown.id, name: shown.name }) }, { kind: 'separator' as const }] : []),
              ...entries(shown),
            ]} /> : null}
            {newFile}
          </div>
        ) : null}
      </header>
      {failure ? (
        <p role="alert" className="mb-[16px] text-[13px] text-label-2">
          {failure}
        </p>
      ) : null}
      {status === 'error' && !shown && phone ? (
        <PhoneEmptyState tone="error" title="Couldn’t load this project" onRetry={retry} />
      ) : status === 'error' && !shown ? (
        <LoadFailed words="This project didn’t load" onRetry={retry} />
      ) : status === 'ready' && !shown && phone ? (
        <PhoneEmptyState title="This project isn’t available" line="It was deleted, or it isn’t shared with you." action={{ label: 'All Projects', onClick: onGone }} />
      ) : status === 'ready' && !shown ? (
        <div className="flex flex-col items-start gap-[8px] py-[16px]">
          <p className="text-[15px] font-medium">This project isn’t available</p>
          <p className="text-[13px] text-label-2">It was deleted, or it isn’t shared with you.</p>
          <Button onClick={onGone}>All Projects</Button>
        </div>
      ) : (
        <>
          <div className="mb-[16px] flex h-[34px] items-center gap-[8px] border-b border-separator">
            <h2 className="flex items-baseline gap-[6px] text-[15px] font-semibold">
              Artworks
              {files !== null ? <span className="text-[13px] font-medium tabular-nums text-label-3">{files}</span> : null}
            </h2>
            <span className="flex-1" />
            <SortMenu options={FILE_SORTS} value={sort} onChange={onSort} />
          </div>
          {children}
        </>
      )}
    </>
  );
}
